<template>
  <!-- 縦に並べて、表だけがスクロールする（見出しは表の上に固定、保存の帯は一番下に固定） -->
  <div class="h-full flex flex-col">
    <!-- 検索に時間がかかるので、枠の真ん中に大きく出す -->
    <div v-if="loading" class="loading-box">Searching...</div>
    <div v-else-if="!loaded" class="empty-text">適用開始日と{{ salesOfficeCaption }}（東京／千葉／埼玉）を選んで「検索」を押してください。</div>
    <div v-else-if="!items.length" class="empty-text">
      <div>対象データがありません。</div>
      <div class="group-note">{{ groupNote }}</div>
    </div>
    <!-- 札・表・保存の帯を表の幅でまとめて画面の左右中央に置く（横に広い画面でも、表と保存ボタンが離れない） -->
    <div v-else class="center-col">
      <!-- 検索した区分の説明（埼玉と千葉の分け方が分かるように） -->
      <div class="group-note">{{ groupNote }}</div>

      <!-- 集計の札 -->
      <div class="chips">
        <div class="chip"><span class="chip-label">対象の行</span><span class="chip-value">{{ items.length }}行</span></div>
        <div class="chip"><span class="chip-label">最低賃金未満</span><span class="chip-value">{{ underCount }}行</span></div>
        <div class="chip"><span class="chip-label">変更する</span><span class="chip-value">{{ checkedCount }}行</span></div>
        <div v-if="noWageCount" class="chip warn"><span class="chip-label">賃金なし</span><span class="chip-value">{{ noWageCount }}行</span></div>
        <div v-if="isNone && noOfficeCount" class="chip warn"><span class="chip-label">{{ salesOfficeCaption }}未選択</span><span class="chip-value">{{ noOfficeCount }}行</span></div>
        <div v-if="noMinWageCount" class="chip warn"><span class="chip-label">最低賃金なし</span><span class="chip-value">{{ noMinWageCount }}行</span></div>
      </div>

      <!-- 行が少なければ表の高さのまま（下に空きを作らない）。多ければ枠いっぱいまで伸びて中でスクロール -->
      <div class="table-wrap min-h-0">
        <table class="bulk-table">
          <thead>
            <tr>
              <!-- 一括チェックは最低賃金未満の行だけ -->
              <th v-if="canEdit"><input type="checkbox" :checked="allUnderChecked" :disabled="!underCount" title="最低賃金未満の行を全部" @change="toggleAll($event.target.checked)" /></th>
              <th class="text-left">物件</th>
              <th class="text-left">清掃員</th>
              <th v-tooltip="salesOfficeTip">{{ salesOfficeCaption }}</th>
              <th>賃金の期間<br /><span class="sub">開始 ～ 終了</span></th>
              <th>時給<br /><span class="sub">{{ slash(startDate) }} 時点</span></th>
              <th>新しい時給</th>
              <th>新しい時給との差<br /><span class="sub">新しい時給 − 時給</span></th>
              <th class="text-left wide">状態</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.key" :class="rowClass(item)">
              <td v-if="canEdit">
                <!-- 賃金なし・営業所未選択・最低賃金なしの行はチェックできない -->
                <input v-if="item.wageId && item.salesOffice && !hasNoMinWage(item)" v-model="item.checked" type="checkbox" @change="onCheck(item)" />
                <input v-else type="checkbox" disabled />
              </td>
              <td class="text-left">
                <div class="site-name">{{ item.siteName || item.cdSite }}</div>
                <div class="sub">契約：{{ item.cdContract || '－' }}</div>
              </td>
              <td class="text-left">
                <div class="staff-name">{{ item.nameStaff || '-' }}</div>
                <div class="sub">（{{ item.staffDailyCleaning }}）</div>
              </td>
              <!-- 最低賃金判定（雇用指示の東京／千葉。「未設定」の検索では行ごとに選び、保存すると雇用指示もその値で埋まる）
                   千葉で物件住所が埼玉県の行は、埼玉の最低賃金と比べる（区分「埼玉」） -->
              <td>
                <template v-if="isNone">
                  <select v-if="canEdit" v-model="item.salesOffice" class="pref-select" :class="item.salesOffice ? 'filled' : ''" @change="onOfficeChange(item)">
                    <option value="">選ぶ</option>
                    <option v-for="o in offices" :key="o" :value="o">{{ o }}</option>
                  </select>
                  <span v-else>{{ item.salesOffice || '不明' }}</span>
                  <div v-if="item.salesOffice" class="sub">最低賃金 {{ yen(minWageOf(item)) }}</div>
                </template>
                <template v-else>
                  {{ item.salesOffice }}
                  <!-- 埼玉の区分の行は物件住所が埼玉県（この画面だけ埼玉の額と比べる）。「埼玉の最低賃金」と書くと他の画面も埼玉と比べていると誤解するので住所で示す -->
                  <div v-if="item.isSaitama" class="sub">物件住所：埼玉県</div>
                </template>
              </td>
              <td class="sub whitespace-nowrap">
                <template v-if="item.wageId">{{ slash(item.wageStartDate) }} ～ {{ slash(item.wageEndDate) }}</template>
                <template v-else>—</template>
              </td>
              <td class="money big">{{ item.wageId ? yen(item.basicWage) : '—' }}</td>
              <td>
                <template v-if="item.wageId && item.salesOffice && (item.checked || isUnder(item))">
                  <template v-if="canEdit">
                    <!-- 下の行を見ようとホイールで流したときに数値が動かないよう、ホイールが来たらフォーカスを外す（ページのスクロールはそのまま） -->
                    <input
                      v-model="item.newWage"
                      type="number"
                      step="1"
                      class="wage-input"
                      :class="errorOf(item) ? 'err' : ''"
                      :disabled="!item.checked"
                      @wheel="$event.target.blur()"
                    /><span class="yen">円</span>
                    <div v-if="item.checked && errorOf(item)" class="err-text">{{ errorOf(item) }}</div>
                  </template>
                  <span v-else class="money">{{ yen(item.newWage) }}</span>
                </template>
                <template v-else>—</template>
              </td>
              <!-- 新しい時給との差（新しい時給 − 時給。チェックして金額が入っている行だけ） -->
              <td :class="diffClass(item)">{{ diffText(item) }}</td>
              <td class="text-left wide">
                <template v-if="!item.wageId">
                  <Badge color="red">賃金なし</Badge> <span class="sub">効いている賃金が無い。登録済み一覧で直してから</span>
                </template>
                <template v-else-if="!item.salesOffice">
                  <Badge color="red">{{ salesOfficeCaption }}を選ぶ</Badge> <span class="sub">住所：{{ judgeAddress(item) || '（空）' }}</span>
                </template>
                <template v-else-if="hasNoMinWage(item)">
                  <Badge color="red">最低賃金なし</Badge> <span class="sub">{{ slash(startDate) }} 時点の {{ groupOf(item) }} の最低賃金がありません。最低賃金マスタに登録してください</span>
                </template>
                <template v-else-if="item.checked && errorOf(item)">
                  <Badge color="red">入力エラー</Badge>
                </template>
                <template v-else-if="item.checked">
                  <Badge color="indigo">変更する</Badge>
                  <span class="sub">{{ planText(item) }}</span>
                </template>
                <template v-else-if="isUnder(item)">
                  <Badge color="yellow">最低賃金未満</Badge> <span class="sub">チェックを外している</span>
                </template>
                <template v-else>
                  <Badge color="gray">すでに最低賃金以上</Badge>
                  <span v-if="isNone" class="sub">住所：{{ judgeAddress(item) }}</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 保存の帯（書き込みできる人だけ。表の下に固定され、スクロールしても見える） -->
      <div v-if="canEdit" class="save-bar flex-shrink-0">
        <span class="msg">{{ checkedCount }} 件の賃金を {{ slash(startDate) }} から変更します</span>
        <span v-if="errorCount" class="text-xs text-red-600 font-bold">入力エラーの行が {{ errorCount }} 行あります。直すまで保存できません。</span>
        <span class="ml-auto flex items-center gap-3">
          <button type="button" class="btn-plain" :disabled="saving" @click="reset">やり直す</button>
          <PrimaryButton text="保存する" class="w-32" :disabled="saving || !checkedCount || errorCount > 0" @click="save" />
        </span>
      </div>
      <p v-else class="text-xs text-gray-500 mt-3">閲覧のみです。変更できるのは人事総務経理に所属している人か、管理者権限がある人だけです。</p>
    </div>
  </div>
</template>

<script>
import Badge from '@components/Badge.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import * as moment from 'moment'
import { SALES_OFFICE_CAPTION, SALES_OFFICE_TIP, MIN_WAGE_OFFICE_NOTE } from '@libs/constantsAKBS'

export default {
  components: {
    Badge,
    PrimaryButton
  },

  props: {
    // 一括変更の対象（賃金1件を1行。賃金が無い雇用指示は wageId 無し）
    rows: {
      type: Array,
      default: () => []
    },
    // 適用開始日の時点の最低賃金（区分（東京／千葉／埼玉） → 金額）
    minWages: {
      type: Object,
      default: () => ({})
    },
    // 雇用指示の最低賃金判定の候補（東京／千葉。未設定の行で選ぶ。埼玉は雇用指示の値ではないので入れない）
    offices: {
      type: Array,
      default: () => []
    },
    // 検索した区分（東京／千葉／埼玉／none）。説明を出すのに使う
    group: {
      type: String,
      default: ''
    },
    // 適用開始日（検索したときの値）
    startDate: {
      type: String,
      default: ''
    },
    // 「営業所なし」の検索か（行ごとに営業所を選ぶ）
    isNone: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    loaded: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 最低賃金判定（東京／千葉）の呼び方と説明
      salesOfficeCaption: SALES_OFFICE_CAPTION,
      salesOfficeTip: SALES_OFFICE_TIP,
      // 画面で触る行（rows を写して、チェック・新しい時給・営業所を持たせる）
      items: []
    }
  },

  computed: {
    underCount() {
      return this.items.filter(i => this.isUnder(i)).length
    },

    checkedCount() {
      return this.items.filter(i => i.checked).length
    },

    noWageCount() {
      return this.items.filter(i => !i.wageId).length
    },

    noOfficeCount() {
      return this.items.filter(i => i.wageId && !i.salesOffice).length
    },

    noMinWageCount() {
      return this.items.filter(i => this.hasNoMinWage(i)).length
    },

    errorCount() {
      return this.items.filter(i => i.checked && this.errorOf(i)).length
    },

    /**
     * 最低賃金未満の行が全部チェックされているか（一括チェックの見た目）
     */
    allUnderChecked() {
      const under = this.items.filter(i => this.isUnder(i))
      return under.length > 0 && under.every(i => i.checked)
    },

    /**
     * 検索した区分の説明（東京／千葉／埼玉の分け方。未設定は行ごとに選ぶこと）
     */
    groupNote() {
      if (this.group === 'none') {
        return `未設定：${this.salesOfficeCaption}が空の雇用指示。行ごとに東京／千葉を選ぶ（千葉で物件住所が埼玉県なら埼玉の最低賃金と比べる）`
      }
      const note = MIN_WAGE_OFFICE_NOTE[this.group]
      return note ? `${this.group}：${note}` : ''
    }
  },

  watch: {
    rows: {
      immediate: true,
      handler() {
        this.reset()
      }
    }
  },

  methods: {
    /**
     * rows から画面の行を作り直す（最低賃金未満の行はチェック済みで、新しい時給に最低賃金を入れる）
     */
    reset() {
      this.items = this.rows.map((r, i) => {
        const item = { ...r, key: `${r.sequenceNumber}-${r.wageId || 'none'}-${i}`, checked: false, newWage: '' }
        this.applyDefault(item)
        return item
      })
    },

    /**
     * 初期のチェックと新しい時給（最低賃金未満ならチェック済み・最低賃金。以上なら未チェック・空）
     * @param {Object} item 行
     */
    applyDefault(item) {
      if (this.isUnder(item)) {
        item.checked = true
        item.newWage = this.minWageOf(item)
      } else {
        item.checked = false
        item.newWage = ''
      }
    },

    /**
     * その行が比べる区分（東京／千葉／埼玉）。最低賃金判定が千葉で物件住所が埼玉県なら埼玉、それ以外は最低賃金判定のまま
     * 未設定の行で選び直したときも、選んだ値と物件住所で決める
     */
    groupOf(item) {
      if (item.salesOffice === '千葉' && item.isSaitama) {
        return '埼玉'
      }
      return item.salesOffice || ''
    },

    /**
     * その行が比べる区分の最低賃金
     */
    minWageOf(item) {
      const group = this.groupOf(item)
      return group ? this.minWages[group] : undefined
    },

    /**
     * 適用開始日の時点でその区分の最低賃金が無いか（賃金と最低賃金判定はあるのにマスタに無い。チェックさせない）
     */
    hasNoMinWage(item) {
      return !!item.wageId && !!item.salesOffice && this.minWageOf(item) === undefined
    },

    /**
     * 最低賃金未満か（賃金があり、区分の最低賃金が分かっていて、時給がそれより低い）
     */
    isUnder(item) {
      const min = this.minWageOf(item)
      return !!item.wageId && min !== undefined && item.basicWage !== null && Number(item.basicWage) < min
    },

    /**
     * チェックを付け外ししたとき（以上の行を手で付けたら今の時給を初期値にする。外したら空に戻す）
     */
    onCheck(item) {
      if (item.checked) {
        if (item.newWage === '' || item.newWage === null) {
          item.newWage = this.isUnder(item) ? this.minWageOf(item) : Number(item.basicWage)
        }
      } else if (!this.isUnder(item)) {
        item.newWage = ''
      }
    },

    /**
     * 未設定の行で最低賃金判定を選んだとき（その区分の最低賃金で初期値を入れ直す）
     */
    onOfficeChange(item) {
      this.applyDefault(item)
    },

    /**
     * 一括チェック（最低賃金未満の行だけ。以上の行は触らない）
     * @param {Boolean} on 付けるか
     */
    toggleAll(on) {
      for (const item of this.items) {
        if (this.isUnder(item)) {
          item.checked = on
          if (on && (item.newWage === '' || item.newWage === null)) {
            item.newWage = this.minWageOf(item)
          }
        }
      }
    },

    /**
     * 新しい時給のエラー（整数でない・最低賃金未満・今の時給より低い・今の時給と同じ）。無ければ空
     */
    errorOf(item) {
      if (!item.checked) {
        return ''
      }
      const wage = Number(item.newWage)
      const min = this.minWageOf(item)
      if (item.newWage === '' || item.newWage === null || !Number.isInteger(wage) || wage <= 0) {
        return '整数で入れてください'
      }
      if (min !== undefined && wage < min) {
        return `${this.yen(min)} 以上にしてください`
      }
      if (item.basicWage !== null && wage < Number(item.basicWage)) {
        return `今の時給 ${this.yen(item.basicWage)} より低くできません`
      }
      // 同じ金額なら変える意味が無い
      if (item.basicWage !== null && wage === Number(item.basicWage)) {
        return `今の時給 ${this.yen(item.basicWage)} と同じです`
      }
      return ''
    },

    /**
     * 変更したときに何が起きるか（前日で閉じて足す／書き換える）
     */
    planText(item) {
      if (item.kind === 'insert') {
        const end = item.wageEndDate ? `～${this.slash(item.wageEndDate)}` : '～'
        return `${this.slash(this.previousDay(this.startDate))} で閉じ、${this.slash(this.startDate)}${end} の賃金を足す`
      }
      return `${this.slash(item.wageStartDate)} からの賃金の時給を書き換える`
    },

    /**
     * 最低賃金判定を選ぶときの手がかりの住所（物件の住所。代勤専門は清掃員の都道府県）
     */
    judgeAddress(item) {
      return item.cdSite === '000000000' ? (item.staffPref || '') : (item.siteAddress || '')
    },

    /**
     * 新しい時給との差（新しい時給 − 今の時給）。チェックしていない・金額が入っていない行は「—」
     */
    diffValue(item) {
      if (!item.checked || !item.wageId || item.basicWage === null || item.newWage === '' || item.newWage === null) {
        return null
      }
      const wage = Number(item.newWage)
      return Number.isInteger(wage) ? wage - Number(item.basicWage) : null
    },

    diffText(item) {
      const diff = this.diffValue(item)
      if (diff === null) {
        return '—'
      }
      if (diff === 0) {
        return '±0円'
      }
      return diff < 0 ? `−${Math.abs(diff).toLocaleString()}円` : `＋${diff.toLocaleString()}円`
    },

    /**
     * 差の色（上がるなら藍色の太字、下がる・同じならエラーと同じ赤）
     */
    diffClass(item) {
      const diff = this.diffValue(item)
      if (diff === null) {
        return 'diff-none'
      }
      return diff > 0 ? 'diff-up' : 'diff-minus'
    },

    rowClass(item) {
      if (!item.wageId || !item.salesOffice || this.hasNoMinWage(item)) {
        return 'attention'
      }
      return item.checked ? 'checked' : ''
    },

    /**
     * 保存（チェックした行を親に渡す。エラーがあれば渡さない）
     */
    save() {
      if (this.errorCount > 0) {
        return
      }
      const picked = this.items
        .filter(i => i.checked)
        .map(i => ({ wageId: i.wageId, salesOffice: i.salesOffice, newWage: Number(i.newWage), nameStaff: i.nameStaff }))
      if (!picked.length) {
        return
      }
      this.$emit('save', picked)
    },

    previousDay(day) {
      return day ? moment(day).subtract(1, 'day').format('YYYY-MM-DD') : ''
    },

    slash(day) {
      return day ? moment(day).format('YYYY/MM/DD') : ''
    },

    yen(val) {
      if (val === null || val === undefined || val === '') {
        return '-'
      }
      return `${Number(val).toLocaleString()}円`
    }
  }
}
</script>

<style scoped>
.empty-text {
  font-size: 0.875rem;
  color: #9ca3af;
}

/* 検索中（枠の真ん中に大きく） */
.loading-box {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  color: #6b7280;
}

/* 検索した区分の説明（札の上） */
.group-note {
  flex-shrink: 0;
  margin: 0 0 8px;
  font-size: 0.8rem;
  font-weight: bold;
  color: #3730a3;
}

/* 札・表・保存の帯のまとまり（表の幅で画面の左右中央。縦は枠に収まる範囲で、表だけがスクロール） */
.center-col {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}

/* 集計の札（登録済み一覧のカードの札と同じ形） */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin: 0 0 12px;
  flex-shrink: 0;
}

.chip {
  display: inline-flex;
  align-items: stretch;
  min-width: 150px;
  border: 1px solid #c7d2fe;
  border-radius: 4px;
  overflow: hidden;
  font-size: 0.8rem;
}

.chip-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  flex-shrink: 0;
  padding: 3px 6px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.7rem;
  white-space: nowrap;
}

.chip-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 3px 10px;
  background: #fff;
  font-weight: bold;
  white-space: nowrap;
}

.chip.warn {
  border-color: #fcd34d;
}

.chip.warn .chip-label {
  background: #fffbeb;
  color: #92400e;
}

/* 表（登録済み一覧の雇用指示の表と同じ作り）。ここだけが縦横にスクロールするので、見出しの sticky が効く */
.table-wrap {
  flex: 0 1 auto;
  max-width: 100%;
  overflow: auto;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
}

.bulk-table {
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  color: #1f2937;
}

.bulk-table th,
.bulk-table td {
  padding: 5px 10px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #d1d5db;
}

.bulk-table th:last-child,
.bulk-table td:last-child {
  border-right: none;
}

.bulk-table tbody tr:last-child td {
  border-bottom: none;
}

.bulk-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #e0e7ff;
  color: #3730a3;
  font-weight: bold;
  font-size: 0.75rem;
  border-bottom-color: #c7d2fe;
}

.bulk-table .text-left {
  text-align: left;
}

.bulk-table td.wide {
  width: 100%;
  white-space: normal;
}

.bulk-table .sub {
  font-size: 0.7rem;
  color: #6b7280;
}

.bulk-table .site-name {
  font-weight: bold;
  color: #1d4ed8;
}

.bulk-table .staff-name {
  font-weight: bold;
  color: #3730a3;
}

.bulk-table .money {
  font-weight: bold;
}

.bulk-table .money.big {
  font-size: 0.95rem;
  color: #1e3a8a;
}

.bulk-table .diff-minus {
  color: #dc2626;
  font-weight: bold;
}

.bulk-table .diff-up {
  color: #3730a3;
  font-weight: bold;
}

.bulk-table .diff-none {
  color: #6b7280;
}

/* 行の種類（変更する行は薄い藍色、賃金なし・都道府県未選択・最低賃金なしは薄い黄色） */
.bulk-table tr.checked td {
  background: #eef2ff;
}

.bulk-table tr.attention td {
  background: #fffbeb;
}

.bulk-table tbody tr:hover td {
  background: #f8faff;
}

.bulk-table tbody tr.checked:hover td {
  background: #e4e9ff;
}

.wage-input {
  width: 96px;
  height: 30px;
  text-align: right;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  font-weight: bold;
}

.wage-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
}

.wage-input.err {
  border-color: #dc2626;
  background: #fbefef;
}

.err-text {
  font-size: 0.7rem;
  color: #dc2626;
  white-space: nowrap;
  margin-top: 2px;
}

.yen {
  font-size: 0.75rem;
  color: #6b7280;
  margin-left: 3px;
}

.pref-select {
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 6px;
  background: #fff;
  font-size: 0.8rem;
}

.pref-select.filled {
  background: #efdf8a;
  font-weight: bold;
}

/* 保存の帯 */
.save-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  background: #eef2ff;
}

.save-bar .msg {
  font-weight: bold;
  color: #3730a3;
}

.btn-plain {
  font-size: 0.8rem;
  color: #2563eb;
  text-decoration: underline;
}

.btn-plain:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}
</style>
